/**
 * Library of canonical NPIs with PMID-cited β-scale factors.
 *
 * Citations are stub-level for v0.1 — every PMID listed here MUST be
 * round-tripped through @biomax/plugin-literature-fetch before any
 * downstream record claims them. Where the literature does not
 * cleanly quantify a single β-scale factor (the common case), the
 * range reflects the published spread + the central value is a
 * pragmatic engine default; verification.note in the parent record
 * documents this.
 *
 * For now the citation notes mark which papers we plan to round-trip
 * during v0.2 catalog curation. Audit gate logs an error if any
 * referenced PMID has not yet been verified.
 */

import type { NpiSpec } from './types.js';

export const NPI_CATALOG: Record<string, NpiSpec> = {
  'mask-universal': {
    id: 'mask-universal',
    label: 'Universal masking (community)',
    betaScale: 0.70,
    betaScaleRange: { low: 0.55, high: 0.85 },
    timeToEffectDays: 7,
    fatigueHalfLifeDays: 90,
    legalAuthority: 'state',
    equityImpact: 'neutral',
    citations: [
      { pmid: '33910820', note: 'meta-analysis of mask effectiveness against respiratory infections (engine-default)' },
    ],
  },
  'mask-targeted': {
    id: 'mask-targeted',
    label: 'Targeted masking (indoor / high-risk settings)',
    betaScale: 0.85,
    betaScaleRange: { low: 0.75, high: 0.95 },
    timeToEffectDays: 3,
    legalAuthority: 'state',
    equityImpact: 'neutral',
    citations: [
      { pmid: '33910820', note: 'subset of mask meta-analysis covering indoor-only deployments' },
    ],
  },
  'distance-tier-1': {
    id: 'distance-tier-1',
    label: 'Distancing tier 1 (advisory; ≥ 1m)',
    betaScale: 0.90,
    betaScaleRange: { low: 0.85, high: 0.95 },
    timeToEffectDays: 3,
    fatigueHalfLifeDays: 30,
    legalAuthority: 'mixed',
    equityImpact: 'neutral',
    citations: [
      { pmid: '32497510', note: 'Chu et al. 2020 Lancet — physical distancing effect on respiratory virus transmission' },
    ],
  },
  'distance-tier-2': {
    id: 'distance-tier-2',
    label: 'Distancing tier 2 (mandated; ≥ 2m + venue caps)',
    betaScale: 0.65,
    betaScaleRange: { low: 0.55, high: 0.80 },
    timeToEffectDays: 5,
    fatigueHalfLifeDays: 60,
    legalAuthority: 'state',
    equityImpact: 'mixed',
    citations: [
      { pmid: '32497510', note: 'Chu et al. 2020 — distance + venue-cap combined effect' },
    ],
  },
  'distance-tier-3': {
    id: 'distance-tier-3',
    label: 'Distancing tier 3 (stay-at-home order; non-essential closure)',
    betaScale: 0.35,
    betaScaleRange: { low: 0.25, high: 0.50 },
    timeToEffectDays: 7,
    fatigueHalfLifeDays: 120,
    legalAuthority: 'state',
    equityImpact: 'negative',
    citations: [
      { pmid: '32555134', note: 'Flaxman et al. 2020 Nature — Europe stay-at-home Rt reductions; engine-fitted to point estimate' },
    ],
  },
  'school-closure': {
    id: 'school-closure',
    label: 'School closure (all levels)',
    betaScale: 0.80,
    betaScaleRange: { low: 0.65, high: 0.95 },
    timeToEffectDays: 7,
    fatigueHalfLifeDays: 180,
    legalAuthority: 'state',
    equityImpact: 'negative',
    citations: [
      { pmid: '32555134', note: 'Flaxman et al. 2020 — school closure component effect estimate' },
    ],
  },
  'workplace-closure': {
    id: 'workplace-closure',
    label: 'Workplace closure (non-essential)',
    betaScale: 0.70,
    betaScaleRange: { low: 0.60, high: 0.85 },
    timeToEffectDays: 7,
    fatigueHalfLifeDays: 90,
    legalAuthority: 'state',
    equityImpact: 'negative',
    citations: [
      { pmid: '32555134', note: 'Flaxman et al. 2020 — workplace closure component' },
    ],
  },
  'quarantine-confirmed': {
    id: 'quarantine-confirmed',
    label: 'Isolation of confirmed cases',
    betaScale: 0.85,
    betaScaleRange: { low: 0.75, high: 0.95 },
    timeToEffectDays: 2,
    legalAuthority: 'local',
    equityImpact: 'neutral',
    citations: [
      { pmid: '32325520', note: 'Hellewell et al. 2020 Lancet GH — contact tracing + isolation feasibility under varying parameters' },
    ],
  },
  'contact-tracing-light': {
    id: 'contact-tracing-light',
    label: 'Contact tracing (light — symptomatic-triggered)',
    betaScale: 0.90,
    betaScaleRange: { low: 0.80, high: 0.97 },
    timeToEffectDays: 5,
    legalAuthority: 'local',
    equityImpact: 'neutral',
    citations: [
      { pmid: '32325520', note: 'Hellewell et al. 2020 — light-tracing scenarios' },
    ],
  },
  'contact-tracing-intensive': {
    id: 'contact-tracing-intensive',
    label: 'Contact tracing (intensive — backward + forward + digital)',
    betaScale: 0.75,
    betaScaleRange: { low: 0.65, high: 0.90 },
    timeToEffectDays: 7,
    legalAuthority: 'mixed',
    equityImpact: 'mixed',
    citations: [
      { pmid: '32325520', note: 'Hellewell et al. 2020 — intensive-tracing scenarios; bolstered by Ferretti et al. 2020 Science (digital tracing)' },
    ],
  },
};

/** All NPI ids in catalog order. */
export function listNpiIds(): string[] {
  return Object.keys(NPI_CATALOG);
}

/** Look up a single NPI spec. Throws if unknown. */
export function getNpi(id: string): NpiSpec {
  const spec = NPI_CATALOG[id];
  if (!spec) throw new Error(`Unknown NPI '${id}'. Available: ${listNpiIds().join(', ')}`);
  return spec;
}
